/**
 * Sentry 错误监控配置
 * 文档: https://docs.sentry.io/platforms/javascript/guides/vue/
 */
import * as Sentry from '@sentry/vue'

/**
 * 初始化 Sentry
 * @param {Object} Vue - Vue 实例
 * @param {Object} router - Vue Router 实例
 * @param {Object} store - Vuex Store 实例
 */
export function initSentry(Vue, router, store) {
  // 只在配置了 DSN 时启用 Sentry
  if (!process.env.VUE_APP_SENTRY_DSN) {
    console.warn('⚠️ Sentry DSN 未配置,错误监控未启用')
    return
  }
  const packageJson = require('../../package.json')
  const isProduction = process.env.NODE_ENV === 'production'
  const isDevelopment = process.env.NODE_ENV === 'development'

  Sentry.init({
    Vue,
    dsn: "https://fb069aab5bfe69f52d28dffe45c39cc2@o4510347059200000.ingest.de.sentry.io/4510357821849680",

    integrations: [
      // 浏览器追踪集成（包含路由追踪）
      Sentry.browserTracingIntegration({
        router,
        // 追踪传播目标（哪些请求需要附加 Sentry 追踪头）
        tracePropagationTargets: [
          'localhost',
          /^https:\/\/chaoren\.asdf\.live/,
          /^https:\/\/91testadminapi\.asdf\.live/,
          /^https:\/\/kefu\.asdf\.live/
        ],
      }),

      // Session Replay - 录制用户会话（发生错误时）
      Sentry.replayIntegration({
        maskAllText: true, // 遮罩所有文本内容（保护用户隐私）
        blockAllMedia: true, // 阻止所有媒体（图片、视频）
        maskAllInputs: true, // 遮罩所有输入框内容
      }),
    ],
     enableLogs: true,
    // ========== 采样率配置 ==========
    // 性能监控采样率（生产环境降低以节省配额）
    tracesSampleRate: isProduction ? 0.2 : 1.0,

    // Session Replay 采样率
    replaysSessionSampleRate: isProduction ? 0.1 : 0.5, // 正常会话采样率
    replaysOnErrorSampleRate: 1.0, // 发生错误时 100% 录制

    // ========== 环境配置 ==========
    environment: process.env.VUE_APP_NODE_ENV || process.env.NODE_ENV || 'production',
    release: `renren-api-movie@${packageJson.version}`,

    // ========== 错误过滤 ==========
    ignoreErrors: [
      // 浏览器扩展引起的错误
      'top.GLOBALS',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'atomicFindClose',

      // 网络相关错误（通常不是代码问题）
      'Non-Error promise rejection captured',
      'Network Error',
      'NetworkError',
      'Failed to fetch',
      'timeout of',
      'Request aborted',

      // ResizeObserver 循环限制（浏览器内部问题,通常无害）
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',

      // 脚本加载错误（通常是网络问题）
      'Loading chunk',
      'ChunkLoadError',
      'Loading CSS chunk',

      // 其他常见无害错误
      'cancelled',
      'Request canceled',
    ],

    // 忽略特定 URL 的错误
    denyUrls: [
      // Chrome 扩展
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      // 其他插件
      /^moz-extension:\/\//i,
    ],

    // ========== 钩子函数 ==========

    // 过滤不需要上报的事务（性能追踪）
    beforeSendTransaction(event) {
      // 过滤掉静态资源请求
      if (event.transaction && event.transaction.match(/\.(js|css|png|jpg|jpeg|gif|svg|woff|woff2|ttf|eot|ico)$/)) {
        return null
      }
      return event
    },

    // 发送前处理错误事件
    beforeSend(event, hint) {
      // 添加用户信息（如果已登录）
      const userInfo = store.state.user
      if (userInfo && userInfo.id) {
        event.user = {
          id: String(userInfo.id),
          username: userInfo.name || 'unknown',
          superAdmin: userInfo.superAdmin === 1
        }
      }

      // 添加应用上下文
      event.contexts = event.contexts || {}
      event.contexts.app = {
        app_version: packageJson.version,
        app_name: 'renren-api-movie',
        build_time: new Date().toISOString(),
        node_env: process.env.NODE_ENV,
        vue_app_env: process.env.VUE_APP_NODE_ENV
      }

      // 添加路由信息
      if (router.currentRoute) {
        event.tags = event.tags || {}
        event.tags.route_name = router.currentRoute.name || 'unknown'
        event.tags.route_path = router.currentRoute.path
        event.tags.route_fullPath = router.currentRoute.fullPath

        // 添加路由参数作为额外上下文
        if (router.currentRoute.params && Object.keys(router.currentRoute.params).length > 0) {
          event.contexts.route = {
            params: router.currentRoute.params,
            query: router.currentRoute.query
          }
        }
      }

      // 在开发环境打印详细错误信息
      if (isDevelopment) {
        console.group('🔴 Sentry 捕获错误')
        console.error('错误对象:', hint.originalException || hint.syntheticException)
        console.log('事件详情:', event)
        console.groupEnd()
      }

      return event
    },

    // 错误发送后的回调
    afterSend(event) {
      if (isDevelopment) {
        console.log('✅ 错误已发送到 Sentry:', event.event_id)
      }
    },
  })

  // 设置全局标签
  Sentry.setTag('app', 'movie-admin-ui')
  Sentry.setTag('framework', 'vue2')
  Sentry.setTag('ui_library', 'element-ui')

  // 设置全局上下文
  Sentry.setContext('runtime', {
    name: 'browser',
    version: navigator.userAgent
  })

  console.log(`✅ Sentry 已初始化 - 环境: ${process.env.VUE_APP_NODE_ENV || process.env.NODE_ENV}`)
}

/**
 * 手动捕获错误
 * @param {Error} error - 错误对象
 * @param {Object} context - 额外上下文
 */
export function captureError(error, context = {}) {
  Sentry.captureException(error, {
    contexts: { additional: context }
  })
}

/**
 * 手动发送消息
 * @param {String} message - 消息内容
 * @param {String} level - 级别: 'info' | 'warning' | 'error'
 */
export function captureMessage(message, level = 'info') {
  Sentry.captureMessage(message, level)
}

/**
 * 添加面包屑（用户行为追踪）
 * @param {Object} breadcrumb - 面包屑对象
 */
export function addBreadcrumb(breadcrumb) {
  Sentry.addBreadcrumb(breadcrumb)
}

/**
 * 设置用户信息
 * @param {Object} user - 用户信息
 */
export function setUser(user) {
  if (user && user.id) {
    Sentry.setUser({
      id: String(user.id),
      username: user.name || user.username,
      superAdmin: user.superAdmin === 1
    })
  } else {
    Sentry.setUser(null)
  }
}

/**
 * 添加标签
 * @param {String} key - 标签键
 * @param {String} value - 标签值
 */
export function setTag(key, value) {
  Sentry.setTag(key, value)
}

export default Sentry
